import React from 'react';
import { FileText, Shield, CheckCircle2 } from 'lucide-react';

export const TermsPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12 pb-24">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs font-semibold">
          <FileText className="w-3.5 h-3.5 text-cyan-400" />
          <span>Legal Documentation</span>
        </div>
        <h1 className="text-4xl font-black text-white tracking-tight">Terms of Service</h1>
        <p className="text-slate-400 text-xs">
          Effective Date: January 1, 2026 | Last Updated: March 2026
        </p>
      </div>

      {/* Main Content */}
      <div className="bg-slate-900/40 rounded-3xl border border-slate-800 p-8 sm:p-12 space-y-8 text-slate-300 text-sm leading-relaxed">
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            <span>1. Acceptance of Terms</span>
          </h2>
          <p>
            By accessing, downloading, or playing any video games, applications, websites, or services provided by <strong>GSR STUDIO</strong> (&ldquo;Studio&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not install or use our services.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-purple-400" />
            <span>2. License Grant & Restrictions</span>
          </h2>
          <p>
            GSR STUDIO grants you a non-exclusive, non-transferable, revocable limited license to download, install, and play our games for personal, non-commercial entertainment purposes.
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-slate-400 text-xs">
            <li>You may not decompile, reverse-engineer, or disassemble game client binaries or shaders.</li>
            <li>You may not develop, host, or use cheats, unauthorized automation tools (bots), exploits, or unauthorized server emulators.</li>
            <li>You may not monetize or sell in-game assets, virtual cosmetics, or player accounts outside authorized marketplace channels.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" />
            <span>3. Community Code of Conduct</span>
          </h2>
          <p>
            Our multiplayer titles thrive on competitive integrity and welcoming communities. All players must refrain from harassment, hate speech, disruptive griefing, intentional match disruption, or toxic behavior. GSR STUDIO reserves the unilateral right to suspend or terminate accounts in violation of these standards.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            <span>4. Intellectual Property</span>
          </h2>
          <p>
            All title, ownership rights, trademarks, graphics, soundtracks, game mechanics, and lore associated with GSR STUDIO and titles such as <em>Neon Odyssey</em>, <em>Chrono Shift</em>, and <em>Velocity Zero</em> remain the exclusive property of GSR STUDIO.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            <span>5. Disclaimer & Limitation of Liability</span>
          </h2>
          <p>
            Our services are provided &ldquo;as is&rdquo; without warranties of any kind. GSR STUDIO shall not be liable for any indirect, incidental, or consequential damages resulting from downtime, cloud synchronisation discrepancies, or software incompatibility.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-400" />
            <span>6. Contact for Legal Inquiries</span>
          </h2>
          <p>
            For inquiries regarding licensing, DMCA notifications, or terms clarification, contact our legal counsel at{' '}
            <span className="text-cyan-400 font-semibold">legal@gsrstudio.games</span>.
          </p>
        </section>
      </div>
    </div>
  );
};
